import { useEffect, useState } from 'react';
import { message } from 'antd';
import {
  sortableContainer,
  sortableElement,
  sortableHandle,
} from 'react-sortable-hoc';

import { BasicButton } from '../../../components/Button';
import * as T from '../../../components/Typography';
import Loading from '../../../components/Loading';
import Icon from 'components/Icon';
import { navRoutes } from '../../../constants';
import { createExerciseUrl } from 'CMS/utils';
import { useExercises } from 'CMS/Providers/ExercisesProvider';
import { useReorderExercises } from '../../../api-calls/exercises.queries';
import handleError from '../../../api-calls/format-error';

const groupByMilestone = (exercises = []) => {
  const groups = new Map();
  exercises.forEach((exercise) => {
    const { milestone } = exercise;
    if (!milestone) return;
    if (!groups.has(milestone.id)) {
      groups.set(milestone.id, { milestone, items: [] });
    }
    groups.get(milestone.id).items.push(exercise);
  });
  return Array.from(groups.values());
};

const moveItem = (items, fromIndex, toIndex) => {
  const updated = [...items];
  const [moved] = updated.splice(fromIndex, 1);
  updated.splice(toIndex, 0, moved);
  return updated;
};

const DragHandle = sortableHandle(() => (
  <Icon
    icon="sixDots"
    width={24}
    height={24}
    color="neutral80"
    mr={3}
    pointer
  />
));

const SortableExercise = sortableElement(({ value, position }) => (
  <li
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 12px',
      marginBottom: '8px',
      border: '1px solid #E0E0E0',
      borderRadius: '8px',
      background: '#fff',
      listStyle: 'none',
    }}
  >
    <DragHandle />
    <T.P weight="bold" m="0">
      {position}
    </T.P>
    <T.Link to={createExerciseUrl(value.id)}>{value.title}</T.Link>
    <T.P color="neutral60" m="0">
      {value.key}
    </T.P>
  </li>
));

const SortableExerciseList = sortableContainer(({ items }) => (
  <ul style={{ padding: 0, margin: 0 }}>
    {items.map((exercise, index) => (
      <SortableExercise
        key={`exercise-${exercise.id}`}
        index={index}
        position={index + 1}
        value={exercise}
      />
    ))}
  </ul>
));

const ViewExercises = () => {
  const { exercises, isLoading } = useExercises();
  const [groups, setGroups] = useState([]);
  const [dirtyMilestoneIds, setDirtyMilestoneIds] = useState({});
  const [savingMilestoneId, setSavingMilestoneId] = useState(null);
  const { mutateAsync: reorderExercises } = useReorderExercises();

  useEffect(() => {
    setGroups(groupByMilestone(exercises));
    setDirtyMilestoneIds({});
  }, [exercises]);

  const handleSortEnd =
    (milestoneId) =>
    ({ oldIndex, newIndex }) => {
      if (oldIndex === newIndex) return;
      setGroups((prev) =>
        prev.map((group) =>
          group.milestone.id === milestoneId
            ? { ...group, items: moveItem(group.items, oldIndex, newIndex) }
            : group
        )
      );
      setDirtyMilestoneIds((prev) => ({ ...prev, [milestoneId]: true }));
    };

  const handleSaveOrder = (group) => async () => {
    setSavingMilestoneId(group.milestone.id);
    try {
      await reorderExercises({
        milestoneId: group.milestone.id,
        orderedExerciseIds: group.items.map((exercise) => exercise.id),
      });
      setDirtyMilestoneIds((prev) => ({
        ...prev,
        [group.milestone.id]: false,
      }));
      message.success(`"${group.milestone.title}" order saved`);
    } catch (error) {
      message.error(
        handleError(error)?.message || 'Could not save the new order'
      );
    } finally {
      setSavingMilestoneId(null);
    }
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <T.H2 mb={2}>Exercises</T.H2>
      <T.P color="neutral60" mb={5}>
        Drag exercises to reorder them within a milestone, then save.
      </T.P>

      {groups.map((group) => (
        <div key={group.milestone.id} style={{ marginBottom: '32px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <T.H3 m="0">{group.milestone.title}</T.H3>
            <div style={{ maxWidth: '180px' }}>
              <BasicButton
                handleClick={handleSaveOrder(group)}
                disabled={
                  !dirtyMilestoneIds[group.milestone.id] ||
                  savingMilestoneId !== null
                }
                loading={savingMilestoneId === group.milestone.id}
              >
                Save order
              </BasicButton>
            </div>
          </div>

          <SortableExerciseList
            items={group.items}
            onSortEnd={handleSortEnd(group.milestone.id)}
            useDragHandle
          />
        </div>
      ))}

      <div style={{ maxWidth: '300px' }}>
        <BasicButton to={navRoutes.CMS.CREATE_EXERCISES} mt={5}>
          Create new exercise
        </BasicButton>
      </div>
    </div>
  );
};

export default ViewExercises;
